import { useMemo, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import ConstructGeometryBoard from "../../../components/measurement/ConstructGeometryBoard";

/**
 * Author editor for the `construction` drawing mode: the student builds the figure on the calibrated
 * board (1 unit = 1 cm) and the server marks it with gradeInteractiveConstruction.
 *
 * Everything is typed as short lines so a tester can author it straight from the question text:
 *   Marking checks   AB = 6        angle BAC = 35        line BC
 *   Measured answers A = 3.4 cm    B = 126°
 *   Printed points   A 1 2         (x y in cm on the board)
 *   Printed lines    A-C           or   1 2 to 13 2   (add "dashed")
 * The model board is the author's own worked answer: it is shown as the "correct construction" on review,
 * and the checks are run against it here so a typo in a check is caught before a student meets it.
 */

const fmt = (n) => String(Math.round(Number(n) * 1000) / 1000);

export const checksToText = (checks) =>
  (Array.isArray(checks) ? checks : [])
    .map((c) => {
      const p = (c?.points || []).join("");
      const tol = c?.tol != null ? ` ± ${fmt(c.tol)}` : "";
      if (c?.type === "angle") return `angle ${p} = ${fmt(c.value)}${tol}`;
      if (c?.type === "segment") return `line ${p}`;
      if (c?.type === "bearing") return `bearing ${p} = ${fmt(c.value)}${tol}`;
      return `${p} = ${fmt(c?.value)}${tol}`;
    })
    .join("\n");

export const parseChecks = (text) => {
  const out = [];
  const errors = [];
  String(text || "")
    .split(/\n|;/)
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((raw) => {
      // Optional per-check tolerance: "BD = 7.79 ± 0.35" (also "+/- 0.35").
      const tm = raw.match(/^(.*?)\s*(?:±|\+\/?-)\s*(\d+(?:\.\d+)?)\s*$/);
      const line = tm ? tm[1].trim() : raw;
      const tolOf = (o) => (tm ? { ...o, tol: parseFloat(tm[2]) } : o);
      let m = line.match(/^(?:angle|∠)\s*([A-Z])\s*([A-Z])\s*([A-Z])\s*=\s*(\d+(?:\.\d+)?)\s*°?$/i);
      if (m) {
        out.push(tolOf({ type: "angle", points: [m[1], m[2], m[3]].map((s) => s.toUpperCase()), value: parseFloat(m[4]) }));
        return;
      }
      m = line.match(/^(?:bearing|direction)\s*([A-Z])\s*-?\s*([A-Z])\s*=\s*(\d+(?:\.\d+)?)\s*°?$/i);
      if (m) {
        out.push(tolOf({ type: "bearing", points: [m[1], m[2]].map((s) => s.toUpperCase()), value: parseFloat(m[3]) }));
        return;
      }
      m = line.match(/^(?:line|join|segment)\s*([A-Z])\s*-?\s*([A-Z])$/i);
      if (m) {
        out.push({ type: "segment", points: [m[1], m[2]].map((s) => s.toUpperCase()) });
        return;
      }
      m = line.match(/^([A-Z])\s*-?\s*([A-Z])\s*=\s*(\d+(?:\.\d+)?)\s*(cm|mm)?$/i);
      if (m) {
        const v = parseFloat(m[3]);
        out.push(tolOf({ type: "length", points: [m[1], m[2]].map((s) => s.toUpperCase()), value: m[4]?.toLowerCase() === "mm" ? v / 10 : v }));
        return;
      }
      errors.push(raw);
    });
  return { checks: out, errors };
};

export const measuresToText = (measures) =>
  (Array.isArray(measures) ? measures : [])
    .map((m) => `${m.label} = ${fmt(m.value)}${m.unit === "°" ? "°" : ` ${m.unit || "cm"}`}`)
    .join("\n");

export const parseMeasures = (text) => {
  const out = [];
  const errors = [];
  String(text || "")
    .split(/\n|;/)
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((line) => {
      const m = line.match(/^(.{1,20}?)\s*=\s*(\d+(?:\.\d+)?)\s*(°|deg|degrees|cm|mm)?$/i);
      if (!m) {
        errors.push(line);
        return;
      }
      const unitRaw = (m[3] || "cm").toLowerCase();
      const unit = unitRaw === "°" || unitRaw.startsWith("deg") ? "°" : unitRaw;
      out.push({ label: m[1].trim(), unit, value: parseFloat(m[2]) });
    });
  return { measures: out, errors };
};

export const givenPointsToText = (pts) =>
  (Array.isArray(pts) ? pts : []).map((p) => `${p[2] || ""} ${fmt(p[0])} ${fmt(p[1])}`.trim()).join("\n");

export const parseGivenPoints = (text) => {
  const out = [];
  const errors = [];
  String(text || "")
    .split(/\n|;/)
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((line) => {
      const m = line.match(/^([A-Z][A-Z0-9']{0,2})\s*[ (]\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*\)?$/i);
      if (m) out.push([parseFloat(m[2]), parseFloat(m[3]), m[1].toUpperCase()]);
      else errors.push(line);
    });
  return { points: out, errors };
};

export const givenSegmentsToText = (segs) =>
  (Array.isArray(segs) ? segs : [])
    .map((s) => (typeof s?.[0] === "string"
      ? `${s[0]}-${s[1]}`
      : `${fmt(s[0][0])} ${fmt(s[0][1])} to ${fmt(s[1][0])} ${fmt(s[1][1])}${s[2] === "dashed" ? " dashed" : ""}`))
    .join("\n");

export const parseGivenSegments = (text) => {
  const out = [];
  const errors = [];
  String(text || "")
    .split(/\n|;/)
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((line) => {
      let m = line.match(/^([A-Z][A-Z0-9']{0,2})\s*-\s*([A-Z][A-Z0-9']{0,2})(\s+dashed)?$/i);
      if (m) {
        out.push([m[1].toUpperCase(), m[2].toUpperCase()]);
        return;
      }
      m = line.match(/^(-?\d+(?:\.\d+)?)[ ,]+(-?\d+(?:\.\d+)?)\s+to\s+(-?\d+(?:\.\d+)?)[ ,]+(-?\d+(?:\.\d+)?)(\s+dashed)?$/i);
      if (m) {
        const seg = [[parseFloat(m[1]), parseFloat(m[2])], [parseFloat(m[3]), parseFloat(m[4])]];
        if (m[5]) seg.push("dashed");
        out.push(seg);
        return;
      }
      errors.push(line);
    });
  return { segments: out, errors };
};

const angleAt = (a, v, b) => {
  const ax = a[0] - v[0], ay = a[1] - v[1], bx = b[0] - v[0], by = b[1] - v[1];
  const den = Math.hypot(ax, ay) * Math.hypot(bx, by);
  if (den < 1e-9) return null;
  return (Math.acos(Math.max(-1, Math.min(1, (ax * bx + ay * by) / den))) * 180) / Math.PI;
};

/** Same algorithm as the server's gradeInteractiveConstruction — used to test the checks on the model. */
export const evaluateConstruction = (spec, value) => {
  const lenTol = Number(spec?.len_tol_cm) || 0.2;
  const angTol = Number(spec?.angle_tol_deg) || 2;
  const requireSeg = spec?.require_segments !== false;
  const given = {};
  (spec?.given_points || []).forEach((g) => { if (g?.[2]) given[String(g[2]).toUpperCase()] = [Number(g[0]), Number(g[1])]; });
  const own = {};
  (value?.points || []).forEach((p, i) => {
    let n = String(p?.[2] || "").toUpperCase();
    if (!n || given[n]) n = `#${i}`;
    own[n] = [Number(p[0]), Number(p[1])];
  });
  const key = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const drawn = new Set((value?.segments || []).map((s) => key(String(s[0]).toUpperCase(), String(s[1]).toUpperCase())));
  (spec?.given_segments || []).forEach((s) => { if (typeof s?.[0] === "string") drawn.add(key(s[0], s[1])); });
  const checks = spec?.checks || [];
  const roles = [...new Set(checks.flatMap((c) => c.points || []).filter((l) => !given[l]))];

  const evalMap = (map) => {
    const pos = (l) => given[l] || (map[l] ? own[map[l]] : null);
    const nm = (l) => (given[l] ? l : map[l]);
    const seg = (a, b) => nm(a) && nm(b) && drawn.has(key(nm(a), nm(b)));
    const fails = [];
    let passed = 0;
    checks.forEach((c) => {
      const p = c.points.map(pos);
      const label = c.points.join("");
      if (p.some((x) => !x)) { fails.push(`${label}: point missing`); return; }
      if (c.type === "segment") { if (seg(c.points[0], c.points[1])) passed += 1; else fails.push(`line ${label} not drawn`); return; }
      if (c.type === "bearing") {
        const dx = p[1][0] - p[0][0], dy = p[1][1] - p[0][1];
        const brg = ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
        const diff = Math.abs(brg - ((c.value + 360) % 360));
        if (Math.hypot(dx, dy) > 1e-9 && Math.min(diff, 360 - diff) <= (c.tol ?? angTol) + 1e-9) passed += 1;
        else fails.push(`bearing ${label} = ${brg.toFixed(0)}°, want ${c.value}`);
        return;
      }
      if (c.type === "length") {
        const d = Math.hypot(p[1][0] - p[0][0], p[1][1] - p[0][1]);
        const ok = Math.abs(d - c.value) <= (c.tol ?? lenTol) + 1e-9 && (!requireSeg || seg(c.points[0], c.points[1]));
        if (ok) passed += 1; else fails.push(`${label} = ${d.toFixed(2)} cm, want ${c.value}`);
        return;
      }
      const deg = angleAt(p[0], p[1], p[2]);
      const ok = deg != null && Math.abs(deg - c.value) <= (c.tol ?? angTol) + 1e-9
        && (!requireSeg || (seg(c.points[1], c.points[0]) && seg(c.points[1], c.points[2])));
      if (ok) passed += 1; else fails.push(`angle ${label} = ${deg == null ? "?" : deg.toFixed(1)}°, want ${c.value}`);
    });
    return { passed, fails };
  };

  const ownNames = Object.keys(own);
  const identity = {};
  roles.forEach((r) => { if (own[r]) identity[r] = r; });
  let best = evalMap(identity);
  if (best.passed < checks.length && roles.length && roles.length <= 6 && ownNames.length >= roles.length) {
    let budget = 60000;
    const rec = (i, map, used) => {
      if (best.passed >= checks.length || budget <= 0) return;
      if (i === roles.length) {
        budget -= 1;
        const r = evalMap(map);
        if (r.passed > best.passed) best = r;
        return;
      }
      ownNames.forEach((n) => { if (!used.has(n)) rec(i + 1, { ...map, [roles[i]]: n }, new Set([...used, n])); });
    };
    rec(0, {}, new Set());
  }
  const measures = (spec?.measures || []).filter((m) => m?.label && Number.isFinite(Number(m.value)));
  let mOk = 0;
  measures.forEach((m) => {
    const raw = value?.measures?.[m.label];
    const got = parseFloat(String(raw ?? "").replace(",", "."));
    const tol = m.tol ?? (m.unit === "°" ? angTol : lenTol);
    if (Number.isFinite(got) && Math.abs(got - Number(m.value)) <= tol + 1e-9) mOk += 1;
    else best.fails.push(`${m.label}: typed ${raw ?? "nothing"}, want ${m.value}${m.unit === "°" ? "°" : ` ${m.unit}`}`);
  });
  return { passed: best.passed + mOk, total: checks.length + measures.length, fails: best.fails };
};

export default function ConstructionSpecEditor({ spec, onChange }) {
  const s = spec || { kind: "construction" };
  const [checksText, setChecksText] = useState(() => checksToText(s.checks));
  const [measuresText, setMeasuresText] = useState(() => measuresToText(s.measures));
  const [pointsText, setPointsText] = useState(() => givenPointsToText(s.given_points));
  const [segsText, setSegsText] = useState(() => givenSegmentsToText(s.given_segments));
  const [modelKey, setModelKey] = useState(0);

  const parsedChecks = useMemo(() => parseChecks(checksText), [checksText]);
  const parsedMeasures = useMemo(() => parseMeasures(measuresText), [measuresText]);
  const parsedPoints = useMemo(() => parseGivenPoints(pointsText), [pointsText]);
  const parsedSegs = useMemo(() => parseGivenSegments(segsText), [segsText]);

  const push = (patch) => {
    const next = { ...s, kind: "construction", ...patch };
    // Distinguishes parts whose student spec is otherwise identical, so each keeps its own board.
    if (!next.board_id) next.board_id = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    onChange(next);
  };

  const result = useMemo(
    () => (s.model && (s.checks?.length || s.measures?.length) ? evaluateConstruction(s, s.model) : null),
    [s]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1.5, border: "1px solid #c7d2fe", borderRadius: 1, bgcolor: "#f8fafc" }}>
      <Typography variant="caption" color="text.secondary">
        The student constructs on a calibrated board (1 unit = 1 cm) with ruler, compass and protractor tools,
        and the drawing is marked automatically. Position and rotation do not matter — only the lengths and
        angles. The question&apos;s own image stays visible above as the rough sketch.
      </Typography>

      <TextField
        size="small"
        multiline
        minRows={3}
        label="Marking checks (one per line)"
        placeholder={"AB = 6\nBC = 8\nangle BAC = 35\nline BC"}
        value={checksText}
        onChange={(e) => {
          setChecksText(e.target.value);
          push({ checks: parseChecks(e.target.value).checks });
        }}
        error={parsedChecks.errors.length > 0}
        helperText={
          parsedChecks.errors.length
            ? `Not understood: ${parsedChecks.errors.join(" | ")}`
            : "“AB = 6” length in cm (or “AD = 56 mm”) · “angle BAC = 35” angle at the middle letter · “line BC” must be drawn · “bearing PQ = 270” direction from P to Q (north is up) · add “± 0.3” to loosen one check. Lengths and angles also require their lines to be drawn."
        }
      />
      <TextField
        size="small"
        multiline
        minRows={1}
        label="Measured answers the student types (optional)"
        placeholder={"A = 3.4 cm\nB = 126°"}
        value={measuresText}
        onChange={(e) => {
          setMeasuresText(e.target.value);
          push({ measures: parseMeasures(e.target.value).measures });
        }}
        error={parsedMeasures.errors.length > 0}
        helperText={parsedMeasures.errors.length ? `Not understood: ${parsedMeasures.errors.join(" | ")}` : "Boxes appear under the board, e.g. “Measure the length marked A”."}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField
          size="small"
          multiline
          minRows={1}
          fullWidth
          label="Printed points (fixed)"
          placeholder="A 1 2"
          value={pointsText}
          onChange={(e) => {
            setPointsText(e.target.value);
            push({ given_points: parseGivenPoints(e.target.value).points });
          }}
          error={parsedPoints.errors.length > 0}
          helperText={parsedPoints.errors.length ? `Not understood: ${parsedPoints.errors.join(" | ")}` : "“A 1 2” = point A at x 1 cm, y 2 cm. For “point A is already marked”."}
        />
        <TextField
          size="small"
          multiline
          minRows={1}
          fullWidth
          label="Printed lines (fixed)"
          placeholder={"1 2 to 13 2\nA-C"}
          value={segsText}
          onChange={(e) => {
            setSegsText(e.target.value);
            push({ given_segments: parseGivenSegments(e.target.value).segments });
          }}
          error={parsedSegs.errors.length > 0}
          helperText={parsedSegs.errors.length ? `Not understood: ${parsedSegs.errors.join(" | ")}` : "“A-C” joins printed points; “1 2 to 13 2 dashed” is a base line."}
        />
      </Stack>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="number" label="Board size (cm)" value={s.board_extent_cm ?? 12}
          onChange={(e) => push({ board_extent_cm: Number(e.target.value) || 12 })}
          inputProps={{ min: 6, max: 30, step: 1 }} sx={{ width: 140 }} />
        <TextField size="small" type="number" label="Length tolerance ± cm" value={s.len_tol_cm ?? 0.2}
          onChange={(e) => push({ len_tol_cm: Number(e.target.value) || 0.2 })}
          inputProps={{ min: 0.05, max: 1, step: 0.05 }} sx={{ width: 170 }} />
        <TextField size="small" type="number" label="Angle tolerance ± °" value={s.angle_tol_deg ?? 2}
          onChange={(e) => push({ angle_tol_deg: Number(e.target.value) || 2 })}
          inputProps={{ min: 0.5, max: 10, step: 0.5 }} sx={{ width: 160 }} />
      </Stack>
      <TextField
        size="small"
        label="Instruction shown to the student (optional)"
        value={s.instructions || ""}
        onChange={(e) => push({ instructions: e.target.value })}
        placeholder="Construct the triangle on the board. Label the corners A, B and C."
      />
      <TextField
        size="small"
        label="Same board as (earlier part's board id)"
        value={s.share_board_with || ""}
        onChange={(e) => push({ share_board_with: e.target.value.trim() })}
        placeholder="hailey23-q26a"
        helperText={`Leave empty for its own board. Set this to an earlier part's board id when this part is marked on that same drawing, the way graph parts share one grid.${s.board_id ? ` This part's own board id: ${s.board_id}` : ""}`}
      />

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Model answer — build the correct construction here (shown to students on review)
          </Typography>
          <Button size="small" color="warning" onClick={() => { push({ model: null }); setModelKey((k) => k + 1); }}>
            Reset model
          </Button>
        </Stack>
        <ConstructGeometryBoard
          key={`model-${modelKey}-${JSON.stringify([s.given_points, s.given_segments, s.board_extent_cm])}`}
          value={s.model || null}
          onChange={(v) => push({ model: v })}
          given={{ points: s.given_points, segments: s.given_segments }}
          measures={s.measures}
          extent={s.board_extent_cm}
          showIntro={false}
          title={null}
        />
        {result ? (
          <Alert severity={result.passed === result.total ? "success" : "warning"} sx={{ py: 0.5 }}>
            Model answer scores {result.passed} / {result.total}
            {result.fails.length ? ` — ${result.fails.join("; ")}` : " — the checks mark it correct."}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Add checks, then build the model answer to test them. Type the readings into the boxes under the board too.
          </Alert>
        )}
      </Box>
    </Box>
  );
}
